/**
 * The drawing over the wall plan — Lite's WallPlanOverlay, the app's LengthMarkers on a WallSide, in
 * the look of the Figma «Wall Plan — Editable» frame: the wall's contour, then the wall's length and
 * the gaps between openings and wall-mounted items above, between floor items below, their heights
 * at the sides and the wall's height at the right. Over them the selection — the wall, a door or a
 * window, an item and, mid-move, what it runs into — and the magnets' guides in the floor plan's
 * red. Pure canvas work on a frame from `state/wallPlan.ts`; `ui/panels/WallPlanOverlay.tsx` owns
 * the canvas and the timing.
 */
import type { Level } from '../../core/hp/types';
import { dimensionBounds, wallPlanSpans, wallPlanToScreen, type WallPlanFrame, type WallPlanSpan } from '../../state/wallPlan';
import type { WallPlanGuide, WallPlanMarks } from '../../state/wallPlanSnap';

/** Pixels between a row and the wall's edge, and between neighbouring rows. */
const ROW = 26;
/** A gap narrower than this on screen keeps its ticks but loses its label; so does one whose label would run into the one before. */
const MIN_LABEL_GAP = 18;
/** The design's ink: dimension lines and labels, and the wall's contour. */
const LINE = '#25262b', CONTOUR = '#474a55';
/** The plan's selection accent (PlanView `COLOR.select`): whatever is picked on the wall wears it. */
const SELECT = '#4085ff';
/** The 3D outline's faint glow, flattened round a selected wall's contour. */
const SELECT_GLOW = 'rgba(64, 133, 255, .22)';
/** The plan's obstruction red (PlanView `COLOR.conflict`), and its magnets' red. */
const CONFLICT = '#ff3b30', GUIDE = '#ee3434';
/** Gap labels sit on a chip of the canvas colour, so the grid never runs through their digits. */
const CHIP = 'rgba(251, 251, 252, .9)';
const FONT = '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif';
/** The whole length and height read large, as in the design; the gaps between things read at body size. */
type Size = 'whole' | 'gap';
const TEXT: Record<Size, { font: string; height: number; offset: number }> = {
  whole: { font: `400 20px ${FONT}`, height: 24, offset: 16 },
  gap: { font: `500 12px ${FONT}`, height: 14, offset: 9 },
};

/** Metres with a decimal comma, the Russian way the design writes them: «5,00». */
export const metres = (value: number): string => value.toFixed(2).replace('.', ',');

export interface WallPlanDrawState {
  /** The selected door or window; outlined when it is on this wall. */
  opening?: string | null;
  /** The shown wall is the selection: its contour wears the accent. */
  wallSelected?: boolean;
  /** The selected item, what a move runs it into and the magnets' guides (`Viewer.wallPlanMarks`). */
  marks?: WallPlanMarks | null;
}

type Axis = (value: number) => number;

export function drawWallPlanOverlay(ctx: CanvasRenderingContext2D, frame: WallPlanFrame, level: Level, state: WallPlanDrawState = {}): void {
  const { wall, height } = frame, length = wall.length;
  const x = (t: number) => wallPlanToScreen(frame, t, 0).x, y = (h: number) => wallPlanToScreen(frame, 0, h).y;
  const left = Math.min(x(0), x(length)), right = Math.max(x(0), x(length));
  if (state.wallSelected) {
    ctx.strokeStyle = SELECT_GLOW; ctx.lineWidth = 8;
    ctx.strokeRect(left, y(height), right - left, y(0) - y(height));
  }
  ctx.strokeStyle = state.wallSelected ? SELECT : CONTOUR; ctx.lineWidth = 2;
  ctx.strokeRect(left, y(height), right - left, y(0) - y(height));
  const picked = state.opening ? level.openings.find(opening => opening.id === state.opening && opening.wallId === wall.id) : undefined;
  if (picked) outline(ctx, x, y, { t0: picked.offset - picked.width / 2, t1: picked.offset + picked.width / 2, h0: picked.sill, h1: picked.sill + picked.height }, SELECT);
  const marks = state.marks;
  for (const span of marks?.conflicts ?? []) outline(ctx, x, y, span, CONFLICT);
  if (marks?.selected) outline(ctx, x, y, marks.selected, marks.selected.conflict ? CONFLICT : SELECT);
  const spans = wallPlanSpans(level, { wallId: wall.id, side: frame.side });
  ctx.lineWidth = 1; ctx.strokeStyle = LINE; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  // Above: the gaps and widths of openings and wall items, then the whole length.
  horizontal(ctx, x, dimensionBounds(spans.upper.map(span => [span.t0, span.t1]), length), y(height) - ROW, 'gap');
  horizontal(ctx, x, [0, length], y(height) - 2 * ROW, 'whole');
  // Below: the floor items standing against the wall.
  horizontal(ctx, x, dimensionBounds(spans.lower.map(span => [span.t0, span.t1]), length), y(0) + ROW, 'gap');
  // At the sides: heights — openings and wall items on the left, floor items on the right, then the wall's own height.
  vertical(ctx, y, dimensionBounds(spans.upper.map(span => [span.h0, span.h1]), height), left - ROW, 'gap', -1);
  vertical(ctx, y, dimensionBounds(spans.lower.map(span => [span.h0, span.h1]), height), right + ROW, 'gap', -1);
  vertical(ctx, y, [0, height], right + 2 * ROW, 'whole', 1);
  for (const guide of marks?.guides ?? []) drawGuide(ctx, x, y, guide);
}

/** A selected thing's rectangle, a few pixels clear of its drawing. */
function outline(ctx: CanvasRenderingContext2D, x: Axis, y: Axis, span: WallPlanSpan, color: string): void {
  const xs = [x(span.t0), x(span.t1)], top = y(span.h1), bottom = y(span.h0);
  ctx.strokeStyle = color; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.roundRect(Math.min(...xs) - 3, top - 3, Math.abs(xs[1] - xs[0]) + 6, bottom - top + 6, 4); ctx.stroke();
}

/**
 * The floor plan's magnet guides on the face: an alignment is a dashed line with small crosses at its
 * ends; an equal gap is a measured line with ticks and its length on a white pill.
 */
/**
 * A 1 px line along an axis, centred where it covers whole device pixels: at an integer position it would
 * otherwise fall across two pixels at half strength and read as a grey smear rather than the guide's red.
 */
function crisp(ctx: CanvasRenderingContext2D, value: number): number {
  const scale = ctx.getTransform?.().a || 1, width = Math.max(1, Math.round(ctx.lineWidth * scale));
  return (width % 2 ? Math.round(value * scale - .5) + .5 : Math.round(value * scale)) / scale;
}

function drawGuide(ctx: CanvasRenderingContext2D, x: Axis, y: Axis, guide: WallPlanGuide): void {
  const a = { x: x(guide.from.t), y: y(guide.from.h) }, b = { x: x(guide.to.t), y: y(guide.to.h) };
  ctx.save();
  ctx.strokeStyle = GUIDE; ctx.lineWidth = 1;
  if (guide.length === undefined) {
    if (Math.abs(a.x - b.x) < .5) a.x = b.x = crisp(ctx, a.x);
    if (Math.abs(a.y - b.y) < .5) a.y = b.y = crisp(ctx, a.y);
    ctx.setLineDash([5, 4]);
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    for (const p of [a, b]) { ctx.moveTo(p.x - 3, p.y); ctx.lineTo(p.x + 3, p.y); ctx.moveTo(p.x, p.y - 3); ctx.lineTo(p.x, p.y + 3); }
    ctx.stroke();
  } else {
    const distance = Math.hypot(b.x - a.x, b.y - a.y);
    if (distance >= 1) {
      const nx = -(b.y - a.y) / distance * 4, ny = (b.x - a.x) / distance * 4;
      ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
      for (const p of [a, b]) { ctx.moveTo(p.x - nx, p.y - ny); ctx.lineTo(p.x + nx, p.y + ny); }
      ctx.stroke();
      if (distance >= 26) {
        const text = metres(guide.length);
        ctx.font = `600 11px ${FONT}`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        const width = ctx.measureText(text).width + 12, cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
        ctx.fillStyle = '#fffffff2'; ctx.beginPath(); ctx.roundRect(cx - width / 2, cy - 10, width, 20, 10); ctx.fill();
        ctx.lineWidth = .6; ctx.stroke();
        ctx.fillStyle = GUIDE; ctx.fillText(text, cx, cy + .5);
      }
    }
  }
  ctx.restore();
}

/** A gaps row with nothing in it would only repeat the whole-length row beside it. */
const empty = (bounds: readonly number[]) => bounds.length < 2 || (bounds.length === 2 && bounds[0] === 0);

function horizontal(ctx: CanvasRenderingContext2D, x: (t: number) => number, bounds: readonly number[], py: number, size: Size): void {
  if (size === 'whole' ? bounds.length < 2 : empty(bounds)) return;
  ctx.beginPath();
  ctx.moveTo(x(bounds[0]), py); ctx.lineTo(x(bounds[bounds.length - 1]), py);
  for (const bound of bounds) {
    const px = x(bound);
    ctx.moveTo(px - 4, py + 5); ctx.lineTo(px + 4, py - 5);
    ctx.moveTo(px, py - 8); ctx.lineTo(px, py + 8);
  }
  ctx.stroke();
  placeLabels(ctx, bounds, x, size, (text, at) => label(ctx, text, at, py - TEXT[size].offset, 0, size));
}

/** `side` −1 puts the labels left of the line, reading upwards; +1 right of it, reading downwards. */
function vertical(ctx: CanvasRenderingContext2D, y: (h: number) => number, bounds: readonly number[], px: number, size: Size, side: -1 | 1): void {
  if (size === 'whole' ? bounds.length < 2 : empty(bounds)) return;
  ctx.beginPath();
  ctx.moveTo(px, y(bounds[0])); ctx.lineTo(px, y(bounds[bounds.length - 1]));
  for (const bound of bounds) {
    const py = y(bound);
    ctx.moveTo(px - 5, py + 4); ctx.lineTo(px + 5, py - 4);
    ctx.moveTo(px - 8, py); ctx.lineTo(px + 8, py);
  }
  ctx.stroke();
  placeLabels(ctx, bounds, y, size, (text, at) => label(ctx, text, px + side * TEXT[size].offset, at, side * Math.PI / 2, size));
}

/**
 * Each gap's label centred on it along the row, in screen order; a label whose chip would run into
 * the one placed before it is left out, so narrow neighbours never print over each other.
 */
function placeLabels(ctx: CanvasRenderingContext2D, bounds: readonly number[], at: (value: number) => number, size: Size, draw: (text: string, centre: number) => void): void {
  ctx.font = TEXT[size].font;
  const labels: Array<{ text: string; centre: number; half: number }> = [];
  for (let i = 1; i < bounds.length; i++) {
    const a = bounds[i - 1], b = bounds[i];
    if (Math.abs(at(b) - at(a)) < MIN_LABEL_GAP) continue;
    const text = metres(b - a);
    labels.push({ text, centre: (at(a) + at(b)) / 2, half: ctx.measureText(text).width / 2 + (size === 'gap' ? 3 : 0) });
  }
  let edge = -Infinity;
  for (const entry of labels.sort((p, q) => p.centre - q.centre)) {
    if (entry.centre - entry.half < edge + 2) continue;
    draw(entry.text, entry.centre);
    edge = entry.centre + entry.half;
  }
}

function label(ctx: CanvasRenderingContext2D, text: string, px: number, py: number, angle: number, size: Size): void {
  ctx.save();
  ctx.translate(px, py);
  if (angle) ctx.rotate(angle);
  ctx.font = TEXT[size].font;
  if (size === 'gap') {
    const width = ctx.measureText(text).width, height = TEXT[size].height;
    ctx.fillStyle = CHIP;
    ctx.beginPath(); ctx.roundRect(-width / 2 - 3, -height / 2, width + 6, height, 4); ctx.fill();
  }
  ctx.fillStyle = LINE;
  ctx.fillText(text, 0, .5);
  ctx.restore();
}
